/* One box for everything: screens, records, reports and settings (N02).
 *
 * People who are new to an ERP do not know the name of the screen they want,
 * so this searches what things are *called* as well as what they are. It is
 * the keyboard-only route through the whole app, and the reason the shell can
 * be navigated without a mouse (N05).
 */
import { useEffect, useMemo, useRef, useState } from 'react'

export type Command = {
  id: string
  label: string
  hint?: string
  group?: string
  /** Multiplies the match score. The curated menu uses it so that typing
   *  "bom" offers **Bills of materials** before "BOM Update Tool": a person
   *  who does not know ERPNext should not have to beat its vocabulary. */
  boost?: number
  run: () => void
}

export function CommandPalette({ commands, onClose, lookUp }: {
  commands: Command[]
  onClose: () => void
  /** Ask the server for real documents matching what has been typed.
   *  Without it the box only finds screens, which is half a search box:
   *  somebody typing a customer's name means the customer, not the menu. */
  lookUp?: (q: string) => Promise<Command[]>
}) {
  const [text, setText] = useState('')
  const [cursor, setCursor] = useState(0)
  const [found, setFound] = useState<Command[]>([])
  const box = useRef<HTMLInputElement>(null)

  useEffect(() => { box.current?.focus() }, [])

  // Documents are looked up as you type; screens are already here. The
  // debounce is short because this is the one thing that has to feel instant.
  useEffect(() => {
    const needle = text.trim()
    if (!lookUp || needle.length < 2) { setFound([]); return }
    let cancelled = false
    const timer = window.setTimeout(() => {
      lookUp(needle)
        .then((hits) => { if (!cancelled) setFound(hits) })
        .catch(() => { if (!cancelled) setFound([]) })
    }, 160)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [text, lookUp])

  const shown = useMemo(() => {
    const needle = text.trim().toLowerCase()
    const scored = commands
      .map((c) => ({ c, score: score(needle, c) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 40)
    // Real documents come after an exact screen match and before the rest:
    // typing "invoices" means the list, typing "SINV-26-00002" means that
    // invoice, and both are one keystroke away.
    const screens = scored.map((x) => x.c)
    if (!found.length) return screens
    return [...screens.slice(0, 3), ...found, ...screens.slice(3)].slice(0, 40)
  }, [commands, text, found])

  useEffect(() => { setCursor(0) }, [text])

  const choose = (index: number) => {
    const picked = shown[index]
    if (!picked) return
    onClose()
    picked.run()
  }

  return (
    <div className="palette-wrap" role="dialog" aria-modal="true" aria-label="Search"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="palette">
        <input
          ref={box}
          value={text}
          placeholder="Search screens, records and settings…"
          aria-label="Search"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose() }
            if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((i) => Math.min(i + 1, shown.length - 1)) }
            if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)) }
            if (e.key === 'Enter') { e.preventDefault(); choose(cursor) }
          }}
        />
        <ul role="listbox">
          {shown.length === 0 ? (
            <li aria-disabled="true" className="muted">Nothing matches “{text}”.</li>
          ) : shown.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === cursor}
                onMouseEnter={() => setCursor(i)} onClick={() => choose(i)}>
              <span className="grow truncate">{c.label}</span>
              {c.hint ? <span className="small muted">{c.hint}</span> : null}
              {c.group ? <span className="chip">{c.group}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** Subsequence matching, so "sinv" finds "Sales Invoice" the way people
 *  actually type when they are in a hurry. An exact prefix still wins. */
function score(needle: string, c: Command): number {
  if (!needle) return c.boost ?? 1
  const hay = `${c.label} ${c.group ?? ''} ${c.hint ?? ''}`.toLowerCase()
  const label = c.label.toLowerCase()
  const hint = (c.hint ?? '').toLowerCase()
  const boost = c.boost ?? 1
  // Typing the exact name of a thing gives you that thing. Without this,
  // "bom" offered **BOM changes** first -- it merely *starts* with the word,
  // while Bills of materials *is* the BOM -- and adding one menu entry was
  // enough to push the real answer down.
  if (label === needle) return 2500 * boost
  if (hint === needle) return 2000 * boost
  if (label.startsWith(needle)) return 1000 * boost
  if (hint.startsWith(needle)) return 900 * boost
  if (label.includes(needle)) return 500 * boost
  if (hay.includes(needle)) return 200 * boost
  let i = 0
  for (const ch of hay) if (ch === needle[i]) i++
  return i === needle.length ? 50 * boost : 0
}
